/**
 * MONSTER TOWN — YOU GOT ONE. FISHING/BUGS CREW (src/catching/**).
 *
 * The one place a catch becomes real, so a fish and a bug are worth exactly the
 * same kind of moment. Six things happen, always in this order:
 *
 *   1. the book records it        -> `collection.found()` (emits collection:found,
 *                                    which the errand engine's free `catch`
 *                                    adapter is already listening to)
 *   2. the bag gets one           -> `inventory.add()`, so catches feed cooking,
 *                                    gifts and the zoo (brief §6, last line)
 *   3. coins on a FIRST find      -> generous, and never taken away
 *   4. a page finished pays again -> "completion is its own reward plus coins"
 *   5. sparkles, a chime, a cheer -> the bit a three-year-old is actually here for
 *   6. the save is asked to run
 *
 * LAW #2 IS STRUCTURAL HERE: there is no failure branch. A duplicate catch is
 * still a happy moment, still counts up, still sparkles — it just does not pay
 * the first-find coins again, because those are for the discovery.
 *
 * THE PAGE BONUS NEEDS NO SAVED STATE, WHICH IS THE POINT. It is paid on the
 * transition — the find that takes a page from incomplete to complete — and
 * `firstTime` is false for ever afterwards, so a reload cannot re-pay it and
 * this crew adds nothing to the save envelope at all. Saves are sacred; the
 * safest new save section is the one that does not exist.
 *
 * AND IT IS PAID OFF THE EVENT, NOT OFF THIS CREW'S CATCHES. A recipe learned
 * in the kitchen and an animal met at the zoo fill in pages of the same book;
 * "completion is its own reward plus coins" (brief §6) has to mean all four
 * pages or it means the two this crew happens to own. `installPageBonus()`
 * listens to `collection:found` and pays whoever completed whatever.
 */

import { collection, type CollectionEntry, type CollectionKind } from '../core/collection'
import { wallet } from '../core/economy'
import { bus } from '../core/events'
import { inventory } from '../core/inventory'
import { save } from '../core/save'
import type { Vec3 } from '../core/types'

/** Coins for finishing a whole page. Big, because finishing one is a big deal. */
const PAGE_BONUS = 60

export interface CatchResult {
  readonly id: string
  readonly firstTime: boolean
  readonly count: number
  readonly coins: number
  readonly pageDone: boolean
  readonly entry: CollectionEntry
}

export function itemIdOf(entry: CollectionEntry): string {
  return entry.itemId ?? entry.id
}

/**
 * THE PAGE BONUS, wherever the find came from. Installed once by the crew entry
 * point; every `collection.found()` in the game passes through here.
 *
 * The transition is read off the event alone: a page can only be COMPLETED by a
 * find that was somebody's FIRST of that creature, so `firstTime && the page is
 * now full` is exactly the moment, with no "before" state to keep and nothing
 * a reload could replay.
 */
export function installPageBonus(playerAt: () => Vec3): () => void {
  return bus.on('collection:found', (e) => {
    if (!e.firstTime) return
    const page = collection.completion(e.kind)
    if (page.total === 0 || page.found !== page.total) return
    const at = e.at ?? playerAt()
    wallet.earnExact(PAGE_BONUS, 'catch.page', at)
    bus.emit('fx:play', { effect: 'stars', at: { x: at.x, y: at.y + 1.2, z: at.z }, count: 18 })
    bus.emit('audio:cue', { cue: 'unlock', at })
    bus.emit('monster:action', { action: 'cheer', seconds: 1.4 })
    save.request('catching:page')
  })
}

/** Everything that happens when a creature is actually caught. Never fails. */
export function claimCatch(entry: CollectionEntry, at: Vec3): CatchResult {
  const before = collection.completion(entry.kind)
  // `found()` emits `collection:found` synchronously, so the page bonus above
  // has already been paid by the time this returns — deliberately, so the two
  // payouts and their two flourishes land together.
  const found = collection.found(entry.id, { at })
  inventory.add(itemIdOf(entry), 1, 'catch')

  const coins = found.coins
  if (coins > 0) wallet.earnExact(coins, 'catch.creature', at)

  const after = collection.completion(entry.kind)
  const pageDone = after.total > 0 && after.found === after.total && before.found < before.total

  // The visible half. `particles`/`sound` here are the same vocabulary a
  // placeable's InteractionResult uses; this is just not a placeable.
  bus.emit('fx:play', {
    effect: found.firstTime ? 'confetti' : 'sparkle',
    at: { x: at.x, y: at.y + 0.6, z: at.z },
    count: found.firstTime ? 14 : 8,
  })
  bus.emit('audio:cue', { cue: found.firstTime ? 'chime_happy' : 'sparkle_soft', at })
  bus.emit('monster:action', { action: found.firstTime ? 'cheer' : 'hop', seconds: 1.2 })
  save.request('catching:found')

  return {
    id: entry.id,
    firstTime: found.firstTime,
    count: found.count,
    coins: coins + (pageDone ? PAGE_BONUS : 0),
    pageDone,
    entry,
  }
}

/** How full a page is. Used by the book and by the tools. */
export function pageState(kind: CollectionKind): { found: number; total: number } {
  return collection.completion(kind)
}
